import { OrderStatus } from '@/interface/order';
import {
  getCreatorContentTypeKey,
  getCreatorPlacementAvailableContentTypes
} from './contentTypes';
import type { CreatorCollaborationPackage, CreatorPlacement } from './model';

export type CreatorInitialPreOfferItem = {
  /** Keep the API's allowed content type, not the legacy display label. */
  contentType: string;
  displayContentType: string;
  dealPrice?: number;
  serviceFee?: number;
  serviceFeeRate?: number;
  subsidyAmount?: number;
  actualGet: number;
};

export type CreatorInitialPreOffer = {
  orderId: string;
  placement: CreatorPlacement;
  offers: CreatorInitialPreOfferItem[];
};

/** A user-confirmed invitation offer, never a negotiate/lastBid request. */
export type CreatorInitialPreOfferResponse = {
  mode: 'accept' | 'counter';
  orderId: string;
  channelKey: string;
  contentType: string;
  amount: number;
};

const finiteAmount = (value: unknown): number | undefined => {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (typeof value === 'string' && !value.trim()) return undefined;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : undefined;
};

const contentTypeKey = getCreatorContentTypeKey;

const displayContentType = (platform: string, value: string) => {
  const labels: Record<string, Record<string, string>> = {
    youtube: {
      dedicatedvideo: 'Dedicated video',
      integratedvideo: 'Integrated video'
    },
    instagram: { reels: 'Reels', post: 'Post' },
    tiktok: { video: 'Video' },
    twitter: { post: 'Post' },
    linkedin: { imagetext: 'Image & Text', video: 'Video', post: 'Post' }
  };
  return labels[platform]?.[contentTypeKey(platform, value)] ?? value;
};

/** Legacy pre-offers are display snapshots, not a second V2 submission flow. */
export function getCreatorInitialPreOffer(
  collaborationPackage: CreatorCollaborationPackage,
  quotePlacements?: CreatorPlacement[]
): CreatorInitialPreOffer | null {
  const order = collaborationPackage.raw;
  if (
    order.status !== OrderStatus.Invited ||
    order.negotiationHistory?.length ||
    order.interestedPriceFields?.length
  )
    return null;
  const active = collaborationPackage.placements.filter(
    (placement) => placement.raw?.active !== false
  );
  // A root-level offer has no account identity; never allocate it to a package
  // or to extra accounts introduced by the account picker.
  if (active.length !== 1) return null;
  const sourcePlacement = active[0];
  // Quote normalization can resolve content type aliases. Match only this
  // original account, while still counting channels on the source order.
  const placement = quotePlacements
    ? quotePlacements.find(
        (item) =>
          sourcePlacement.channelKey &&
          item.channelKey === sourcePlacement.channelKey &&
          item.raw?.active !== false
      )
    : sourcePlacement;
  if (!placement || placement.platform !== sourcePlacement.platform)
    return null;
  const platform = placement.platform.trim().toLowerCase();
  if (
    order.platform &&
    String(order.platform).trim().toLowerCase() !== platform
  )
    return null;
  const history = order.ahaPicksOffer?.history;
  const latest = Array.isArray(history) ? history[history.length - 1] : null;
  if (!latest || typeof latest !== 'object' || Array.isArray(latest))
    return null;
  const availableTypes = getCreatorPlacementAvailableContentTypes(placement);
  const offers: CreatorInitialPreOfferItem[] = [];
  for (const field of Object.values(latest) as any[]) {
    if (!field || typeof field.contentType !== 'string') continue;
    if (field.channelKey && field.channelKey !== placement.channelKey) continue;
    if (
      field.platform &&
      String(field.platform).trim().toLowerCase() !== platform
    )
      continue;
    const contentType =
      availableTypes.find((type) => type === field.contentType) ??
      availableTypes.find(
        (type) =>
          contentTypeKey(platform, type) ===
          contentTypeKey(platform, field.contentType)
      );
    const actualGet = finiteAmount(field.actualGet);
    if (!contentType || actualGet === undefined || actualGet <= 0) continue;
    if (offers.some((offer) => offer.contentType === contentType)) continue;
    offers.push({
      contentType,
      displayContentType: displayContentType(platform, field.contentType),
      actualGet,
      dealPrice: finiteAmount(field.dealPrice),
      serviceFee: finiteAmount(field.serviceFee),
      serviceFeeRate: finiteAmount(field.serviceFeeRate),
      subsidyAmount: finiteAmount(field.subsidyAmount)
    });
  }
  return offers.length
    ? { orderId: collaborationPackage.id, placement, offers }
    : null;
}

export function getCreatorInitialPreOfferSelection(
  preOffer: CreatorInitialPreOffer,
  selection?: {
    orderId: string;
    placementId: string;
    contentType: string;
  } | null
): CreatorInitialPreOfferItem {
  const selectedType =
    selection?.orderId === preOffer.orderId &&
    selection.placementId === preOffer.placement.id
      ? selection.contentType
      : preOffer.placement.contentType;
  return (
    preOffer.offers.find((offer) => offer.contentType === selectedType) ??
    preOffer.offers.find(
      (offer) =>
        selectedType &&
        contentTypeKey(preOffer.placement.platform, offer.contentType) ===
          contentTypeKey(preOffer.placement.platform, selectedType)
    ) ??
    preOffer.offers[0]
  );
}

export function getCreatorInitialPreOfferResponse(
  collaborationPackage: CreatorCollaborationPackage,
  selection: Parameters<typeof getCreatorInitialPreOfferSelection>[1],
  mode: CreatorInitialPreOfferResponse['mode']
): CreatorInitialPreOfferResponse | null {
  const preOffer = getCreatorInitialPreOffer(collaborationPackage);
  if (!preOffer?.placement.channelKey) return null;
  if (mode === 'counter' && !collaborationPackage.raw.isPriceNegotiable)
    return null;
  const item = getCreatorInitialPreOfferSelection(preOffer, selection);
  return {
    mode,
    orderId: preOffer.orderId,
    channelKey: preOffer.placement.channelKey,
    contentType: item.contentType,
    amount: item.actualGet
  };
}

/** Reject expired sessions rather than silently accepting a replacement offer. */
export function isCreatorInitialPreOfferResponseCurrent(
  collaborationPackage: CreatorCollaborationPackage,
  response: CreatorInitialPreOfferResponse
): boolean {
  const preOffer = getCreatorInitialPreOffer(collaborationPackage);
  return Boolean(
    preOffer &&
      response.orderId === preOffer.orderId &&
      response.channelKey === preOffer.placement.channelKey &&
      (response.mode === 'accept' ||
        (response.mode === 'counter' &&
          collaborationPackage.raw.isPriceNegotiable)) &&
      preOffer.offers.some(
        (offer) =>
          contentTypeKey(preOffer.placement.platform, offer.contentType) ===
            contentTypeKey(preOffer.placement.platform, response.contentType) &&
          offer.actualGet === response.amount
      )
  );
}
